import * as z from 'zod'

const boardItemSchema = z.object({
  assistantNames: z.array(z.string().min(1)),
  assistantUserIds: z.array(z.string().min(1)),
  date: z.string(),
  doctorName: z.string().min(1),
  endTime: z.string(),
  institutionName: z.string().min(1),
  location: z.string().nullable(),
  regionalManagerName: z.string().nullable(),
  regionalManagerUserId: z.string().min(1).nullable(),
  startTime: z.string(),
  status: z.enum(['CANCELLED', 'OBSOLETE', 'SCHEDULED']),
  weekday: z.number().int().min(1).max(7),
})

const boardResponseSchema = z.object({
  endDate: z.string(),
  items: z.array(boardItemSchema),
  startDate: z.string(),
})

const boardIdentityExchangeSchema = z.object({
  success: z.literal(true),
  userid: z.string().min(1),
})

const sdkSignatureSchema = z.object({
  agentConfigNonceStr: z.string().min(1),
  agentConfigSignature: z.string().regex(/^[a-f\d]{40}$/iu),
  agentConfigTimestamp: z.union([z.number().int(), z.string().regex(/^\d+$/u)]),
  agentId: z.number().int().positive(),
  corpId: z.string().min(1),
  nonceStr: z.string().min(1),
  signature: z.string().regex(/^[a-f\d]{40}$/iu),
  timestamp: z.union([z.number().int(), z.string().regex(/^\d+$/u)]),
})

export type WeComScheduleBoardResponse = z.infer<typeof boardResponseSchema>
export type WeComScheduleBoardItem = z.infer<typeof boardItemSchema>
export type WeComSdkSignature = z.infer<typeof sdkSignatureSchema>

function apiOrigin() {
  if (false) {
    return globalThis.location.origin
  }
  return new URL('https://api-test.nly.life' || '/', globalThis.location.origin).origin
}

async function requestJson(path: string, init: RequestInit = {}) {
  const response = await fetch(new URL(path, apiOrigin()), {
    ...init,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...init.headers,
    },
  })
  const payload: unknown = await response.json().catch(() => undefined)
  if (!response.ok) {
    const message = typeof payload === 'object' && payload !== null && 'error' in payload
      && typeof payload.error === 'object' && payload.error !== null && 'message' in payload.error
      && typeof payload.error.message === 'string'
      ? payload.error.message
      : '看板数据暂时不可用'
    throw new Error(message)
  }
  return payload
}

export async function fetchScheduleBoard() {
  const path = false
    ? '/proxy/public/wecom/schedule-board'
    : '/public/wecom/schedule-board'
  return boardResponseSchema.parse(await requestJson(path))
}

export async function fetchScheduleBoardIdentityStart(redirectUri: string) {
  const path = false
    ? '/proxy/public/wecom/schedule-board/identity/start'
    : '/public/wecom/schedule-board/identity/start'
  const query = new URLSearchParams({ redirect_uri: redirectUri })
  const payload = await requestJson(`${path}?${query.toString()}`)
  return z.object({ url: z.string().url() }).parse(payload)
}

export async function exchangeScheduleBoardIdentity(code: string, state: string, redirectUri: string) {
  const path = false
    ? '/proxy/public/wecom/schedule-board/identity/exchange'
    : '/public/wecom/schedule-board/identity/exchange'
  return boardIdentityExchangeSchema.parse(await requestJson(path, {
    body: JSON.stringify({ code, redirect_uri: redirectUri, state }),
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
  }))
}

export async function fetchWeComSdkSignature(url: string) {
  const path = false
    ? '/proxy/public/wecom/schedule-board/sdk-signature'
    : '/public/wecom/schedule-board/sdk-signature'
  const query = new URLSearchParams({ url })
  return sdkSignatureSchema.parse(await requestJson(`${path}?${query.toString()}`))
}
